import { useCallback, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Renders a dropdown panel into <body> and pins it to its anchor.
 *
 * Dropdowns used to be absolutely positioned inside the field. Inside a modal the
 * form body is a short `overflow-y-auto` box, so an open list was clipped by it and
 * the field scrolled out of view behind the panel. Portalling to the body takes the
 * panel out of that scroll box; the position is recalculated on scroll and resize so
 * it stays glued to the field.
 */
const GAP = 8;

const PopoverLayer = ({ anchorRef, panelRef, matchAnchorWidth = false, children }) => {
    const [style, setStyle] = useState(() => ({ position: 'fixed', visibility: 'hidden', zIndex: 200 }));

    const place = useCallback(() => {
        const anchor = anchorRef.current;
        if (!anchor) return;

        const rect = anchor.getBoundingClientRect();
        const panelHeight = panelRef.current?.offsetHeight || 0;
        const spaceBelow = window.innerHeight - rect.bottom - GAP;
        const spaceAbove = rect.top - GAP;
        const flipUp = panelHeight > 0 && panelHeight > spaceBelow && spaceAbove > spaceBelow;

        // A tall panel (the date + time picker) used to be pinned by its bottom edge,
        // so flipping up pushed its header off the top of the screen. Positioning by
        // `top` and clamping to the viewport keeps it fully on screen; whatever no
        // longer fits scrolls inside the panel instead of being cut off.
        setStyle({
            position: 'fixed',
            left: Math.max(GAP, Math.min(rect.left, window.innerWidth - rect.width - GAP)),
            top: flipUp ? Math.max(GAP, rect.top - GAP - panelHeight) : rect.bottom + GAP,
            width: matchAnchorWidth ? rect.width : undefined,
            maxHeight: Math.max(flipUp ? spaceAbove : spaceBelow, 200),
            overflowY: 'auto',
            visibility: 'visible',
            zIndex: 200,
        });
    }, [anchorRef, panelRef, matchAnchorWidth]);

    // Layout effect so the measured position is applied before the browser paints —
    // the panel never shows up in the wrong spot first.
    useLayoutEffect(() => {
        place();
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);
        return () => {
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
        };
    }, [place]);

    return createPortal(<div style={style}>{children}</div>, document.body);
};

export default PopoverLayer;
